import { memo } from 'react';
import { ArrowUpRight, Check, X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';

import { SentimentBadge } from '@/components/SentimentBadge';
import { SignalBadge } from '@/components/SignalBadge';
import { MetricCard } from '@/features/stock-details/MetricCard';
import type { EnrichedOpportunity } from '@/models/marketData';
import {
  formatCurrency,
  formatIstNewsTime,
  formatNumber,
  formatPercent,
} from '@/utils/formatters';
import { getSignalLabel, sentimentType } from '@/utils/marketCalculations';
import './stock-detail-drawer.css';

interface StockDetailDrawerProps {
  opportunity: EnrichedOpportunity | null;
  onClose: () => void;
}

export const StockDetailDrawer = memo(({ opportunity, onClose }: StockDetailDrawerProps) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const selectedKey = opportunity?.tradeInstrument.instrumentKey;

  useEffect(() => {
    if (!selectedKey) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusable = [
        ...drawerRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose, selectedKey]);

  if (!opportunity) return null;
  const { tradeInstrument, indicators, metrics } = opportunity;
  const news = [...opportunity.relatedNews].sort(
    (a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt),
  );
  const details = [
    ['Model signal', getSignalLabel(opportunity.signal)],
    ['Setup score', formatNumber(opportunity.score, 0)],
    ['Confidence', formatPercent(opportunity.confidence)],
    ['News context', metrics.newsSentiment.label],
  ] as const;

  return (
    <div className="drawer-layer">
      <button
        type="button"
        className="drawer-backdrop"
        onClick={onClose}
        aria-label="Close stock details"
      />
      <aside
        ref={drawerRef}
        className="stock-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stock-drawer-title"
      >
        <header className="stock-drawer__header">
          <div>
            <span className="eyebrow">TradeXRadar · Research intelligence · Non-advisory</span>
            <div className="drawer-symbol-row">
              <h2 id="stock-drawer-title">{tradeInstrument.symbol}</h2>
              <SignalBadge signal={opportunity.signal} />
            </div>
            <p>{tradeInstrument.name}</p>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close details">
            <X size={18} />
          </button>
        </header>
        <div className="stock-drawer__body">
          <section className="instrument-hero">
            <div className="instrument-monogram" aria-hidden="true">
              {tradeInstrument.symbol.slice(0, 2)}
            </div>
            <div>
              <span className="eyebrow">{tradeInstrument.exchange} · Research snapshot</span>
              <h3>{tradeInstrument.name}</h3>
            </div>
            <div className="snapshot-price">
              <span>Last evaluated close</span>
              <strong>{formatCurrency(indicators.close)}</strong>
              <small>Snapshot price · not a live quote</small>
            </div>
          </section>
          <DetailSection title="Quick insight summary">
            <div className="metric-grid summary-grid">
              {details.map(([label, value]) => (
                <MetricCard label={label} value={value} key={label} />
              ))}
            </div>
            <p className="section-note">
              Model outputs describe this snapshot. Confidence is not a probability of investment
              returns.
            </p>
          </DetailSection>
          <DetailSection title="Decision evidence">
            <div className="evidence-grid">
              <Evidence
                title="Constructive evidence"
                tone="positive"
                items={opportunity.reasons.filter((reason) => /:\s*\+\d+\s*$/.test(reason))}
                empty="No positive scoring evidence supplied."
              />
              <Evidence
                title="Risk notes / caution"
                tone="warning"
                items={opportunity.reasons.filter((reason) => /:\s*-\d+\s*$/.test(reason))}
                empty="No negative scoring reasons supplied. This does not mean the setup is risk-free."
              />
              <Evidence
                title="Technical context"
                items={[
                  'EMA structure: ' + (metrics.emaAligned ? 'fully aligned' : 'mixed'),
                  'SuperTrend: ' + (indicators.superTrendBullish ? 'positive' : 'negative'),
                  'Volume participation: ' +
                    formatNumber(indicators.volumeRatio20, 2) +
                    '× average',
                  'RSI: ' +
                    formatNumber(indicators.rsi14, 1) +
                    (indicators.rsi14 >= 70 ? ' · elevated' : ''),
                  ...opportunity.reasons.filter((reason) => !/:\s*[+-]\d+\s*$/.test(reason)),
                ]}
                empty="No additional context supplied."
              />
              <Evidence
                title="Before drawing conclusions"
                items={[
                  'Verify the latest price and market conditions independently.',
                  'Review liquidity, costs, and your own risk constraints.',
                  'Scenario levels are illustrative, not forecasts or recommendations.',
                  'News coverage may be incomplete or delayed.',
                ]}
                empty=""
              />
            </div>
          </DetailSection>
          <DetailSection title="Scenario levels">
            <p className="section-note">
              Reference levels from the model for illustration only. Not advice or an instruction to
              trade.
            </p>
            <div className="trade-plan" aria-label="Illustrative scenario levels">
              <MetricCard
                label="Watch zone reference"
                value={formatCurrency(opportunity.suggestedEntry)}
              />
              <MetricCard
                label="Risk reference"
                value={formatCurrency(opportunity.stopLoss)}
                tone="negative"
              />
              <MetricCard
                label="Upside scenario 1"
                value={formatCurrency(opportunity.target1)}
                tone="positive"
              />
              <MetricCard
                label="Upside scenario 2"
                value={formatCurrency(opportunity.target2)}
                tone="positive"
              />
            </div>
          </DetailSection>
          <DetailSection title="Technical telemetry">
            <div className="metric-grid metric-grid--technical">
              <MetricCard label="EMA 20" value={formatCurrency(indicators.ema20)} />
              <MetricCard label="EMA 50" value={formatCurrency(indicators.ema50)} />
              <MetricCard label="EMA 200" value={formatCurrency(indicators.ema200)} />
              <MetricCard
                label="EMA structure"
                value={metrics.emaAligned ? 'Fully aligned' : 'Mixed'}
                tone={metrics.emaAligned ? 'positive' : 'warning'}
              />
              <MetricCard
                label="RSI 14"
                value={formatNumber(indicators.rsi14, 1)}
                tone={indicators.rsi14 >= 70 ? 'warning' : undefined}
              />
              <MetricCard
                label="ADX 14"
                value={formatNumber(indicators.adx14, 1)}
                tone={indicators.adx14 >= 25 ? 'positive' : undefined}
              />
              <MetricCard label="ATR 14" value={formatNumber(indicators.atr14, 2)} />
              <MetricCard
                label="Volume ratio"
                value={`${formatNumber(indicators.volumeRatio20, 2)}×`}
                tone={indicators.volumeRatio20 >= 1.5 ? 'positive' : undefined}
              />
              <MetricCard label="MACD" value={formatNumber(indicators.macd, 2)} />
              <MetricCard label="MACD signal" value={formatNumber(indicators.macdSignal, 2)} />
              <MetricCard
                label="MACD state"
                value={
                  metrics.freshMacdCrossover
                    ? 'Fresh bullish cross'
                    : metrics.macdBullish
                      ? 'Bullish'
                      : 'Below signal'
                }
                tone={metrics.macdBullish ? 'positive' : 'warning'}
              />
              <MetricCard
                label="SuperTrend"
                value={`${formatCurrency(indicators.superTrend)} · ${indicators.superTrendBullish ? 'Bullish' : 'Bearish'}`}
                tone={indicators.superTrendBullish ? 'positive' : 'negative'}
              />
              <MetricCard
                label="Momentum ready"
                value={metrics.momentumReady ? 'Yes' : 'No'}
                tone={metrics.momentumReady ? 'positive' : undefined}
              />
            </div>
          </DetailSection>

          <DetailSection title={`Related news · ${news.length}`}>
            <div className="drawer-news-list">
              {news.length === 0 && <p className="empty-copy">No related news supplied.</p>}
              {news.map((article) => (
                <article className="drawer-news-card" key={article.articleLink}>
                  {article.thumbnail ? (
                    <img src={article.thumbnail} alt="" loading="lazy" />
                  ) : (
                    <div className="news-image-fallback">NEWS</div>
                  )}
                  <div>
                    <SentimentBadge
                      type={sentimentType(article.sentiment)}
                      label={`${article.sentiment > 0 ? '+' : ''}${formatNumber(article.sentiment, 2)}`}
                    />
                    <h4>{article.heading}</h4>
                    <p>{article.shortSummary || article.summary}</p>
                    <footer>
                      <span>{formatIstNewsTime(article.publishedAt)}</span>
                      <a href={article.articleLink} target="_blank" rel="noopener noreferrer">
                        Read story <ArrowUpRight size={13} />
                      </a>
                    </footer>
                  </div>
                </article>
              ))}
            </div>
          </DetailSection>

          <DetailSection title="Instrument">
            <div className="instrument-key">
              {tradeInstrument.instrumentKey} · {tradeInstrument.exchange}
            </div>
          </DetailSection>
          <p className="popup-disclaimer">
            For informational and educational use only. Not investment advice, research advice, or a
            recommendation to buy, sell, or hold any security. Scenario levels are illustrative.
            Investments are subject to market risks. Verify independently.
          </p>
        </div>
      </aside>
    </div>
  );
});

const DetailSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="detail-section">
    <h3>{title}</h3>
    {children}
  </section>
);

const Evidence = ({
  title,
  items,
  empty,
  tone = '',
}: {
  title: string;
  items: string[];
  empty: string;
  tone?: string;
}) => (
  <article className={'evidence-card evidence-card--' + tone}>
    <h4>{title}</h4>
    {items.length ? (
      <ul>
        {items.map((item, index) => (
          <li key={index}>
            <Check size={15} aria-hidden="true" />
            <span>{item.replace(/:\s*[+-]\d+\s*$/, '')}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p>{empty}</p>
    )}
  </article>
);
